/**
 * What the patient is told before the injection coach touches a camera.
 *
 * Held as data rather than as copy inside a screen for three reasons.
 *
 * Clinical and legal review reads a list of claims instead of a rendered sheet,
 * so what was approved is unambiguous. The claims are versioned, so a consent
 * given today is tied to what was actually disclosed today and a later edit
 * cannot retroactively change what she agreed to. And a test pins the set, so
 * nobody can quietly add, remove or soften a claim — `disclosure.test.ts` fails
 * until the version is bumped alongside.
 *
 * Every claim here must be true of the shipped build. That is the whole point:
 * `nothing-is-recorded` is not a promise the product intends to keep, it is a
 * statement about code that has no path to keep a recording at all. See
 * `no-retention.test.ts`, which asserts the absence rather than trusting it.
 */

/**
 * Bumped whenever the claim set changes in meaning.
 *
 * Date-based rather than semantic: a disclosure is not a dependency, it is a
 * record of what was said on a day.
 */
export const COACH_DISCLOSURE_VERSION = "2026-09-15.1";

/**
 * The claims the disclosure makes, in the order they are shown.
 *
 * Each maps to one sentence the patient reads. Split this finely — a claim that
 * bundles two statements cannot be reviewed, translated or withdrawn separately.
 */
export type CoachDisclosureClaim =
  /**
   * The coach is software, not a nurse. Required by EU AI Act transparency
   * obligations for systems that interact with people, and by #2267's fifth
   * safety principle, which forbids the coach impersonating a clinician.
   */
  | "guidance-comes-from-software-not-a-nurse"
  /**
   * The approved instructions remain authoritative. The coach restates an
   * approved protocol; it does not author one and it cannot change a dose.
   */
  | "does-not-replace-your-clinic-instructions"
  /**
   * The camera is optional and the coach works fully without it. This is a
   * product commitment (#2267 principle 7) and the thing that keeps the consent
   * genuinely free rather than a condition of access — consent obtained as the
   * price of a service is not freely given.
   */
  | "camera-is-optional-and-coaching-works-without-it"
  /** What the camera is for, stated before it is ever switched on. */
  | "camera-only-helps-during-the-step-you-are-on"
  /**
   * The load-bearing claim. Nothing is written down: no video, no audio, no
   * still frames, no transcript.
   */
  | "nothing-is-recorded-or-saved"
  /** Nothing reaches the care team, so she is not being observed at work. */
  | "nothing-is-shared-with-your-clinic"
  /**
   * Withdrawal is one tap and takes effect immediately, which GDPR Article 7(3)
   * requires to be as easy as giving consent was.
   */
  | "you-can-turn-the-camera-off-at-any-moment"
  /** The coach is not an emergency channel and must never be mistaken for one. */
  | "not-for-emergencies-call-your-clinic-or-emergency-services";

export type CoachDisclosure = {
  version: string;
  claims: readonly CoachDisclosureClaim[];
};

/**
 * Ordered deliberately: what this is, what it is not, then the camera, then what
 * happens to what the camera sees, then how to stop, then the emergency line.
 *
 * She is told what the thing is before she is told what it wants, and told what
 * happens to the footage before she is asked to allow any. Informing after the
 * permission prompt would be informing after collection.
 *
 * ### The reading voice is deliberately not among these
 *
 * A review asked for a ninth claim, on the grounds that the voice says her
 * medication and her dose out loud in whatever room she is standing in, and
 * that nothing here mentions sound. Reviewed and declined, deliberately:
 *
 * - **Nothing is captured.** The voice only speaks. No microphone is opened,
 *   no audio is recorded, kept or sent — `pickVoice` disqualifies any voice
 *   that synthesises off-device, so the words never leave the phone. "Rien
 *   n'est enregistré ni conservé. Ni vidéo, ni son, ni photo" stays exactly
 *   true: what it promises is that *her* sound is not taken.
 * - **It says her own data back to her**, the brand and dose already printed on
 *   the step she is reading, on a device she is holding.
 * - **It is a setting, not a collection.** Off by default, on by one tap, off
 *   by the same tap. A consent sheet is for what the product takes from her;
 *   putting a volume control in it would teach her to read these eight
 *   sentences as boilerplate, which is the one thing they cannot become.
 *
 * If the voice ever records, transcribes or transmits, that stops being true
 * and this list gets its ninth claim and a new version on the same day.
 */
export const COACH_DISCLOSURE: CoachDisclosure = {
  version: COACH_DISCLOSURE_VERSION,
  claims: [
    "guidance-comes-from-software-not-a-nurse",
    "does-not-replace-your-clinic-instructions",
    "camera-is-optional-and-coaching-works-without-it",
    "camera-only-helps-during-the-step-you-are-on",
    "nothing-is-recorded-or-saved",
    "nothing-is-shared-with-your-clinic",
    "you-can-turn-the-camera-off-at-any-moment",
    "not-for-emergencies-call-your-clinic-or-emergency-services",
  ],
};

/** The i18n key carrying the sentence for a claim. */
export function disclosureClaimKey(claim: CoachDisclosureClaim): string {
  return `care.injection_coach.disclosure.${claim.replaceAll("-", "_")}`;
}
